import { RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { LearningDevelopmentModal } from '../components/LearningDevelopmentModal';
import { learningDevelopmentApi } from '../learningDevelopmentApi';
import type {
  LearningDevelopmentCatalog,
  LearningDevelopmentEntry,
} from '../learningDevelopmentTypes';

/**
 * One of the four L&D vocabularies — what the personnel profile's Training/L&D panel describes
 * each record with.
 *
 * **One page for all four**, parameterised by the catalog descriptor. They are one shape and
 * one administrative job, so four copies of this file would be four places for a column to be
 * added to three of. See `LearningDevelopmentCatalog`.
 *
 * The standard management flow: index, create, profile, edit, delete. The anchor opens the
 * **profile**, not the edit form — every index page in this product is partnered with one, and
 * here the profile is where the count of L&D records already filed under a row is worth reading
 * before rewording it or retiring it.
 */
export function LearningDevelopmentIndexPage({ catalog }: { catalog: LearningDevelopmentCatalog }) {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<LearningDevelopmentEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    entry: LearningDevelopmentEntry;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchEntries = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      learningDevelopmentApi.list(
        catalog.slug,
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [catalog.slug],
  );

  const rowActions = useCallback(
    (entry: LearningDevelopmentEntry): MenuItem[] => [
      {
        key: `edit-${entry.id}`,
        label: 'Edit',
        permission: 'learning-development.update',
        onClick: () => setEditing(entry),
      },
      {
        key: `restore-${entry.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'learning-development.delete',
        disabledReason: entry.isActive ? `This ${catalog.noun} is already active.` : null,
        onClick: () => setConfirming({ kind: 'activate', entry }),
      },
    ],
    [catalog.noun],
  );

  // Rebuilt per catalog because the anchor's header is the vocabulary's own word. Memoised so
  // the shell's column resolution is not handed a new array on every render.
  const columns = useMemo(() => buildColumns(catalog), [catalog]);

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await learningDevelopmentApi.remove(catalog.slug, confirming.entry.id);
      } else {
        await learningDevelopmentApi.restore(catalog.slug, confirming.entry.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<LearningDevelopmentEntry>
        /*
          The MODULE is `learning-development` for all four, which is what the settings cog's
          History Logs and Clear data are keyed on. The four vocabularies share one key set
          because maintaining them is one administrative job.
        */
        module="learning-development"
        /*
          The shell derives its noun from the module key, and this is the one module where that
          is wrong: four vocabularies share the key, so "Clear all learning development" would
          misstate the scope of the widest destructive action the page has. It clears THIS list.
        */
        moduleNoun={catalog.nounPlural}
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          catalog.label,
        ]}
        columns={columns}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={[ENTITY_TYPES[catalog.slug]]}
        rowKey={(entry) => entry.id}
        fetch={fetchEntries}
        reloadKey={reloadKey}
        searchPlaceholder={`Search ${catalog.nounPlural}`}
        emptyMessage={
          catalog.slug === 'sponsors'
            ? 'No sponsors yet. This list is this employer’s own — it fills as L&D records name who conducted them.'
            : `No ${catalog.nounPlural} yet. Add the ones this organization uses.`
        }
        onCreate={() => setCreating(true)}
        createPermission="learning-development.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(entry) => navigate(`${catalog.settingsPath}/${entry.id}`)}
        openPermission="learning-development.read"
        rowActions={rowActions}
        onDelete={(entry) => setConfirming({ kind: 'deactivate', entry })}
        deletePermission="learning-development.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(entry) =>
          entry.isActive ? null : `This ${catalog.noun} is already deleted.`
        }
        // Clears THIS vocabulary only — the entry sits on this page and clears what it lists.
        onClearData={() => learningDevelopmentApi.clear(catalog.slug)}
        clearDataPermission="learning-development.clear-data"
        otherInfo={(entry) => entry}
      />

      {creating || editing !== null ? (
        <LearningDevelopmentModal
          catalog={catalog}
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'deactivate' ? `Delete ${catalog.noun}` : `Restore ${catalog.noun}`
          }
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`ld-entry-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.entry.name}" stops being offered on the Training/L&D form.`
                  : `"${confirming.entry.name}" is offered again on the Training/L&D form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.entry.trainingCount > 0 ? (
                <p>
                  {confirming.entry.trainingCount} L&amp;D record
                  {confirming.entry.trainingCount === 1 ? '' : 's'}
                  {confirming.entry.trainingCount === 1 ? ' is' : ' are'} already filed under it.
                  Those records keep it and still state it — retiring a {catalog.noun} does not
                  rewrite what was recorded.
                </p>
              ) : null}

              <p>
                Nothing is erased — a deleted {catalog.noun} can be restored from this list.
              </p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The audit entity behind each slug.
 *
 * Four tables, four CLR names, so History Logs on one vocabulary's page shows that vocabulary's
 * trail rather than all four — the one place the four genuinely are separate records.
 */
const ENTITY_TYPES: Record<LearningDevelopmentCatalog['slug'], string> = {
  types: 'LearningDevelopmentType',
  categories: 'LearningDevelopmentCategory',
  interventions: 'DevelopmentIntervention',
  sponsors: 'TrainingSponsor',
};

function buildColumns(catalog: LearningDevelopmentCatalog): ColumnDef<LearningDevelopmentEntry>[] {
  return [
    {
      // The name is the anchor: a vocabulary row is referred to by its word, never by its
      // position.
      key: 'name',
      header: catalog.columnHeader,
      width: 260,
      sortable: true,
      render: (entry) => <TextCell value={entry.name} />,
    },
    {
      key: 'description',
      header: 'Description',
      width: 420,
      flex: true,
      summary: true,
      render: (entry) => <TextCell value={entry.description} />,
    },
    {
      // The stored sort value, under a heading that says so: a POSITION is only correct with
      // the whole ladder in hand, and a paged table never has it.
      key: 'sortOrder',
      header: 'Order',
      width: 90,
      align: 'right',
      sortable: true,
      render: (entry) => <TextCell value={String(entry.sortOrder)} mono numeric />,
    },
    {
      key: 'trainingCount',
      header: 'L&D records',
      width: 120,
      align: 'right',
      sortable: true,
      summary: true,
      // The count is of personnel records, which is another module's data — this row's own
      // fields beside it are this module's and stay ungated.
      permission: 'personnel.read',
      render: (entry) => <TextCell value={String(entry.trainingCount)} mono numeric />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (entry) =>
        entry.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];
}
